<template>
  <div class="rules-page">
    <div class="container">
      <h2 class="mt-4">{{ $t('ruleTitle') }}</h2>

      <!-- Success and Error Messages -->
      <div v-if="successMessage" class="alert alert-success fixed-alert">{{ successMessage }}</div>
      <div v-if="errorMessage" class="alert alert-danger fixed-alert">{{ errorMessage }}</div>

      <!-- Add Rule Button -->
      <button class="btn btn-primary" @click="openModal('add')">
        <i class="bi bi-plus-circle"></i> {{ $t('addRule') }}
      </button>

      <!-- Rules Table -->
      <table v-if="rules.length" class="table table-hover mt-3">
        <thead>
        <tr>
          <th>{{ $t('ID') }}</th>
          <th>{{ $t('ruleName') }}</th>
          <th>{{ $t('manager') }}</th>
          <th>{{ $t('maxAbsentMembers') }}</th>
          <th>{{ $t('ruleMembers') }}</th>
          <th>{{ $t('actions') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="rule in rules" :key="rule.id || rule.name">
          <td>{{ rule.id }}</td>
          <td>{{ rule.name }}</td>
          <td>{{ rule.manager }}</td>
          <td>{{ rule.max_absent_members }}</td>
          <td>
            <ul>
              <li v-for="member in rule.included_users" :key="member.id">
                {{ member.full_name }}
              </li>
            </ul>
          </td>
          <td>
            <button class="btn btn-primary btn-sm" @click="openModal('edit', rule)">{{ $t('edit')}}</button>
            <button class="btn btn-danger btn-sm" @click="openConfirmDelete(rule.id)">{{ $t('delete')}}</button>
          </td>
        </tr>
        </tbody>
      </table>
      <div v-else class="alert alert-warning">{{ $t('noRules')}}</div>
    </div>

    <!-- Modal for Add/Edit Rule -->
    <div class="modal fade" id="ruleModal" tabindex="-1" aria-labelledby="ruleModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="ruleModalLabel">{{ isEditMode ? $t('updateRule') : $t('addRule') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitRule">
              <div class="mb-3">
                <label for="ruleName" class="form-label">{{ $t('ruleName') }}</label>
                <input v-model="ruleForm.name" type="text" class="form-control" id="ruleName" required/>
              </div>
              <div class="mb-3">
                <label for="maxAbsent" class="form-label">{{ $t('maxAbsentMembers') }}</label>
                <input v-model="ruleForm.max_absent_members" type="number" class="form-control" id="maxAbsent"
                       required/>
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('selectMembers') }}</label>
                  <div v-for="user in availableUsers" :key="user.id" class="form-check">
                    <input v-model="ruleForm.included_users" type="checkbox" class="form-check-input" :value="user.id"
                           :checked="ruleForm.included_users.includes(user.id)"/>
                    <label class="form-check-label">{{ user.full_name }}</label>
                  </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="submitRule">{{
                isEditMode ? $t('update') : $t('add')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('confirmDeleteTitle') }}</h3>
        <p>{{ $t('confirmDeleteRule') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteRule">{{ $t('confirmDelete') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMainStore } from '@/store';
import axios from 'axios';

export default {
  data() {
    return {
      managerId: localStorage.getItem('userID'),
      rules: [],
      availableUsers: [],
      ruleForm: {id: null, name: '', max_absent_members: 0, included_users: []},
      successMessage: '',
      errorMessage: '',
      isEditMode: false,
      showConfirm: false,
      ruleToDelete: null,
      modalInstance: null
    };
  },
  computed: {
    store: () => useMainStore(),
  },
  created() {
    this.fetchRules();
    this.fetchUsers();
    this.$nextTick(() => {
      const modalElement = document.getElementById("ruleModal");
      if (modalElement) {
        this.modalInstance = new bootstrap.Modal(modalElement);
      }
    });
  },
  methods: {
    submitRule() {
      if (this.isEditMode) {

        this.updateRule();
      } else {
        this.addRule();
      }
    },
    addRule() {
      const url = '/api/v1/admin/rules/';
      const payload = {
        name: this.ruleForm.name,
        max_absent_members: this.ruleForm.max_absent_members,
        included_users: this.ruleForm.included_users.map(user => user.id || user),
        manager: this.store.user?.id
      };

      console.log("Submitting new rule:", payload);

      axios.post(url, payload)
          .then(() => {
            this.successMessage = "Rule added successfully.";
            this.fetchRules();
            this.closeModal();
          })
          .catch(error => {
            console.error("Error adding rule:", error.response?.data || error);
            this.errorMessage = "Failed to add rule: " + JSON.stringify(error.response?.data || error);
          })
          .finally(() => {
            setTimeout(() => {
              this.successMessage = "";
              this.errorMessage = "";
            }, 2000);
          });
    },
    updateRule() {
      const url = `/api/v1/admin/rules/${this.ruleForm.id}/`;

      // Step 1: Clear previously selected users
      axios.patch(url, {included_users: []})
          .then(() => {
            // Step 2: Update rule details and add only the newly selected users
            console.log(this.ruleForm.included_users)
            const payload = {
              name: this.ruleForm.name,
              max_absent_members: this.ruleForm.max_absent_members,
              included_users: this.ruleForm.included_users.filter(user => !user.id).map(user => user.id || user)
            };
            console.log("Updating rule with selected users:", payload);

            return axios.patch(url, payload);
          })
          .then(() => {
            this.successMessage = "Rule updated successfully.";
            this.fetchRules();
            this.closeModal();
          })
          .catch(error => {
            console.error("Error updating rule:", error.response?.data || error);
            this.errorMessage = "Failed to update rule: " + JSON.stringify(error.response?.data || error);
          })
          .finally(() => {
            setTimeout(() => {
              this.successMessage = "";
              this.errorMessage = "";
            }, 2000);
          });
    },
    fetchUsers() {
      axios.get(`/api/v1/admin/users/${this.managerId}/direct-reports/`)
          .then(response => {
            this.availableUsers = response.data.results || response.data;
          })
          .catch(error => {
            console.error("Error fetching users:", error);
            this.errorMessage = "Failed to fetch users.";
          });
    },
    fetchRules() {
      axios.get('/api/v1/admin/rules/')
          .then(response => {
            this.rules = response.data.results || response.data || [];
          })
          .catch(error => {
            console.error("Error fetching rules:", error);
            this.errorMessage = "Failed to fetch rules.";
          });
    },
    openModal(mode, rule = null) {
      if (mode === "add") {
        this.isEditMode = false;
        this.ruleForm = {
          id: null,
          name: '',
          max_absent_members: 0,
          included_users: []
        };
      } else if (mode === "edit" && rule) {
        this.isEditMode = true;
        this.ruleForm = {...rule};
      }
      if (this.modalInstance) {
        this.modalInstance.show();
      }
    },
    closeModal() {
      if (this.modalInstance) {
        this.modalInstance.hide();
      }
    },
    openConfirmDelete(ruleId) {
      this.ruleToDelete = ruleId;
      this.showConfirm = true;
    },
    cancelDelete() {
      this.showConfirm = false;
      this.ruleToDelete = null;
    },
    deleteRule() {
      axios.delete(`/api/v1/admin/rules/${this.ruleToDelete}/`)
          .then(() => {
            this.successMessage = "Rule deleted successfully.";
            this.fetchRules();
          })
          .catch(error => {
            console.error("Error deleting rule:", error);
            this.errorMessage = "Failed to delete rule.";
          })
          .finally(() => {
            this.cancelDelete();
          });
    }
  }
};
</script>
